import appVersionInfo from '../../version.json';
import { getDebugEnabled } from '../../features/scrabble/services/settingsService';
import { generateId } from '../../utils/generateId';

const ANALYTICS_URL = process.env.REACT_APP_ANALYTICS_API_URL || '';
const ANALYTICS_KEY = process.env.REACT_APP_ANALYTICS_API_KEY || '';
const ENABLED = process.env.REACT_APP_ANALYTICS_ENABLED === 'true';
const APP_VERSION = appVersionInfo.version;

const PLAYER_ID_KEY = 'analytics.playerId.v1';
const BATCH_THRESHOLD = 20;
const MAX_BATCH_POST_SIZE = 50;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 200;

let queue = [];
let flushTimerId = null;
let unloadBound = false;
let cachedPlayerId = null;

let contextSnapshot = {
  roundIndex: 1,
  actIndex: 1,
  roundInAct: 1,
  gamePhase: 'playing',
  bagPreset: 'classic',
  score: 0,
  runPoints: 0,
};

let currentRunId = null;

const canUseStorage = () => {
  try {
    return typeof window !== 'undefined' && Boolean(window.localStorage);
  } catch {
    return false;
  }
};

export const updateAnalyticsContext = (patch) => {
  Object.assign(contextSnapshot, patch);
};

export const setRunId = (id) => {
  currentRunId = id;
};

const getRunId = () => currentRunId;

const getPlayerId = () => {
  if (cachedPlayerId) return cachedPlayerId;
  if (canUseStorage()) {
    const stored = window.localStorage.getItem(PLAYER_ID_KEY);
    if (stored) {
      cachedPlayerId = stored;
      return cachedPlayerId;
    }
  }
  const newId = generateId();
  if (canUseStorage()) {
    try {
      window.localStorage.setItem(PLAYER_ID_KEY, newId);
    } catch {}
  }
  cachedPlayerId = newId;
  return cachedPlayerId;
};

const postEvents = (events) => {
  if (!ANALYTICS_URL || !events.length) return;
  if (getDebugEnabled()) console.log('[Analytics] POST', { url: ANALYTICS_URL, events });
  fetch(ANALYTICS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(ANALYTICS_KEY ? { 'x-api-key': ANALYTICS_KEY } : {}),
    },
    body: JSON.stringify({ events }),
    keepalive: true,
  }).catch(() => {});
};

const beaconEvents = (events) => {
  if (
    !ANALYTICS_URL ||
    !events.length ||
    typeof navigator === 'undefined' ||
    typeof navigator.sendBeacon !== 'function'
  ) {
    return;
  }
  if (getDebugEnabled()) console.log('[Analytics] Beacon', { url: ANALYTICS_URL, events });
  navigator.sendBeacon(
    ANALYTICS_URL,
    new Blob([JSON.stringify({ events })], { type: 'application/json' })
  );
};

const drainQueue = (useBeacon) => {
  if (!queue.length) return;
  const batch = queue.splice(0, MAX_BATCH_POST_SIZE);
  if (useBeacon) {
    beaconEvents(batch);
  } else {
    postEvents(batch);
  }
};

const bindUnloadHandlers = () => {
  if (unloadBound || typeof document === 'undefined' || typeof window === 'undefined') return;
  unloadBound = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') drainQueue(true);
  });
  window.addEventListener('beforeunload', () => drainQueue(true));
};

const startAutoFlush = () => {
  if (flushTimerId !== null || typeof window === 'undefined') return;
  flushTimerId = window.setInterval(() => drainQueue(false), FLUSH_INTERVAL_MS);
  bindUnloadHandlers();
};

export const enqueueEvent = (eventName, payload, contextOverrides) => {
  if (!ENABLED || !ANALYTICS_URL) return;

  startAutoFlush();

  queue.push({
    eventId: generateId(),
    eventName,
    eventVersion: 2,
    occurredAt: new Date().toISOString(),
    playerId: getPlayerId(),
    runId: getRunId(),
    appVersion: APP_VERSION,
    clientPlatform: 'web',
    context: { ...contextSnapshot, ...(contextOverrides || {}) },
    payload: payload || {},
  });

  if (queue.length > MAX_QUEUE_SIZE) {
    queue.splice(0, queue.length - MAX_QUEUE_SIZE);
  }

  if (queue.length >= BATCH_THRESHOLD) {
    drainQueue(false);
  }
};
